import React, { useState, useEffect } from 'react';
import { Button, Box, Stack, Paper, Divider, Typography } from '@mui/material';
import TopBar from 'components/dashboardParts/TopBar';
import APIRequest, { APIError, readStreamAsText } from 'system/APIRequest';
import PasswordConfirmation from 'components/profilePage/PasswordConfirmation';

import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from "react-router-dom";

export default function PasswordReset() {
  const { i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();

  const requestParams = new URLSearchParams(location.search).get('request') || "";
  const emailParams = new URLSearchParams(location.search).get('email') || "";

  const [password, setPassword] = useState("");
  const [passwordIsValid, setIsPasswordValid] = useState(false);
  const [passwordInFlight, setPasswordInFlight] = useState(false);

  const [resetComplete, setResetComplete] = useState(false);

  // If the reset is complete, redirect to the login page
  useEffect(() => {
    if (resetComplete) {
      const timer = setTimeout(() => {
        navigate("/login", { replace: true });
      }, 1000);

      // Cleanup the timer if the component unmounts before the timeout completes
      return () => clearTimeout(timer);
    }
  }, [resetComplete]);

  function handlePasswordChange(isValid: boolean, password: string): void {
    setPassword(password);
    setIsPasswordValid(isValid);
  }

  function resetPassword() {
    if (!password || !passwordIsValid) {
      return;
    }

    setPasswordInFlight(true);

    APIRequest(`iforgot/${requestParams}`, {email: emailParams}, {method: "POST", body: password}).then(() => {
      enqueueSnackbar(i18n.t('login.password.password-reset-in-flight'), {variant: 'success'});
      setResetComplete(true);
    }).catch(async (error: APIError) => {
      const bodyText = await readStreamAsText(error.body.body);
      enqueueSnackbar(`${bodyText}`, {variant: 'error', persist: true});
    }).finally(() => {
      setPasswordInFlight(false);
    });
  }

  return (
    <Box sx={{maxWidth:"1300px", margin:"0 auto", position:"relative"}}>
      <TopBar title={i18n.t('login.password.topbar-title')} subTitle={emailParams} showLogo={true} hideAvatar={true}/>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center"
        }}>
        <Stack
          direction="column"
          spacing={2}
          sx={{
            padding: 2,
            width: 700,
            maxWidth: 700
          }}>
          <Paper sx={{ padding: 2, width: "100%"}}>
            <Stack direction="column" spacing={2} sx={{
              padding: 2
            }}>
              {requestParams &&
                <Box>
                  <Typography variant="h4" sx={{ marginBottom: "20px" }}>
                    {i18n.t("login.password.password-reset-header")}
                  </Typography>
                  <Divider sx={{ marginBottom: "20px" }}/>

                  <PasswordConfirmation
                    passwordLabel="New Password"
                    onPasswordChange={handlePasswordChange}
                  />

                  <Stack
                    direction={"row"}
                    spacing={1}
                    sx={{
                      justifyContent: "end",
                      marginTop: "20px"
                    }}>
                    <Button
                      disabled={!passwordIsValid}
                      variant="contained"
                      color="primary"
                      onClick={resetPassword}
                      loading={passwordInFlight}
                    >
                      {i18n.t("login.password.reset-button")}
                    </Button>
                  </Stack>
                </Box>
              }
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
}
